/**
 * dsh-work taskboard — Host 新控制器 API 适配层。
 *
 * 上游 0.1.2-alpha.1 起移除了 apiProxy 包（commit 4f00a8b8 "remove ApiProxy
 * package"），session/workspace API 重构进 session/workspace 控制器 +
 * agentPresets 服务。runner/routes 仍按旧 apiProxy 信封
 * （{result:{ok,value|error}}）编程，本层把它们转回新服务直调语义：
 *
 *   - 旧 sessions.history 翻页 → follow 首帧快照取 cursor（log cut）+
 *     page 向前翻；cursor 按会话缓存，仅同一结算流程内使用；
 *   - 旧 result 信封 → 新服务直接 throw（TypertRemoteFailure 等），统一
 *     转信封；
 *   - prompt 新增必填 requestId，由本层代发。
 *
 * 只消费官方公开服务；任一服务缺席返回 undefined，调用方降级。
 */

const RPC_TIMEOUT_MS = 30_000
const MAX_CURSORS = 128

/** 把新服务抛出的失败转回旧信封的 error 形状。 */
function envelope(error) {
  if (error !== null && typeof error === 'object' && error.name === 'TypertRemoteFailure'
    && typeof error.failure?.code === 'string' && typeof error.failure.message === 'string') {
    return { code: error.failure.code, message: error.failure.message }
  }
  return { code: 'internal', message: error instanceof Error ? error.message : String(error) }
}

/**
 * runner/routes 按旧 apiProxy 调用姿态传 {rpcId, payload}（见 runner.js 的
 * request()）；新控制器直调只认参数本体，统一解包。
 */
function unpack(request) {
  return request !== null && typeof request === 'object' && 'payload' in request ? request.payload : request
}

/**
 * 组装旧 apiProxy 信封面的适配器。
 * @param {object} ctx 插件上下文（sessionController/workspaceRegistry/agentPresets 软探测）。
 * @returns {object | undefined} 信封面；sessionController 缺席时 undefined。
 */
export function createTaskboardHostApi(ctx) {
  const sessionController = ctx.get('sessionController')
  if (sessionController === undefined) return undefined
  const workspaceRegistry = ctx.get('workspaceRegistry')
  const agentPresets = ctx.get('agentPresets')
  // 同一执行结算流程内翻页需要第一页 follow 帧携带的 cursor（throughSeq）。
  // 会话已停止时 cursor 稳定，运行中的会话不翻页，缓存无并发风险。
  const cursors = new Map()

  function takeCursor(sessionId) {
    const cursor = cursors.get(sessionId)
    if (cursors.size > MAX_CURSORS) cursors.clear()
    return cursor
  }

  return {
    workspace: {
      async list() {
        if (workspaceRegistry === undefined) {
          return { result: { ok: false, error: { code: 'unavailable', message: 'workspace service unavailable' } } }
        }
        try {
          return {
            result: {
              ok: true,
              value: {
                items: workspaceRegistry.list().map(workspace => ({
                  workspaceId: workspace.id,
                  title: workspace.title,
                  path: workspace.path,
                })),
              },
            },
          }
        } catch (error) {
          return { result: { ok: false, error: envelope(error) } }
        }
      },
    },
    agentPresets: {
      async list() {
        if (agentPresets === undefined) {
          return { result: { ok: false, error: { code: 'unavailable', message: 'agent presets service unavailable' } } }
        }
        try {
          const presets = await agentPresets.list()
          const defaultId = agentPresets.defaultId
          return {
            result: {
              ok: true,
              value: {
                presets: presets.map(preset => ({
                  id: preset.id,
                  trust: preset.trust,
                  isDefault: preset.id === defaultId,
                  ...(preset.name === undefined ? {} : { name: preset.name }),
                  ...(preset.description === undefined ? {} : { description: preset.description }),
                  ...(preset.broken === undefined ? {} : { broken: preset.broken }),
                })),
              },
            },
          }
        } catch (error) {
          return { result: { ok: false, error: envelope(error) } }
        }
      },
    },
    sessions: {
      async create(request) {
        try {
          const value = await sessionController.create(unpack(request))
          return { result: { ok: true, value } }
        } catch (error) {
          return { result: { ok: false, error: envelope(error) } }
        }
      },
      async rename(request) {
        try {
          return { result: { ok: true, value: await sessionController.rename(unpack(request)) } }
        } catch (error) {
          return { result: { ok: false, error: envelope(error) } }
        }
      },
      async prompt(request) {
        try {
          const { sessionId, mode, content } = unpack(request)
          const value = await sessionController.prompt(
            { requestId: crypto.randomUUID(), sessionId, mode, content },
            AbortSignal.timeout(RPC_TIMEOUT_MS),
          )
          return { result: { ok: true, value } }
        } catch (error) {
          return { result: { ok: false, error: envelope(error) } }
        }
      },
      async list() {
        try {
          const value = await sessionController.list({}, AbortSignal.timeout(RPC_TIMEOUT_MS))
          return { result: { ok: true, value } }
        } catch (error) {
          return { result: { ok: false, error: envelope(error) } }
        }
      },
      async history(request) {
        const { sessionId, maxMessages, beforeSeq } = unpack(request)
        const address = { kind: 'session', sessionId }
        try {
          if (beforeSeq === undefined) {
            // 第一页：follow 首帧快照携带 cursor（log cut）与最新一页记录。
            const follow = sessionController.follow(
              { address, maxMessages },
              AbortSignal.timeout(RPC_TIMEOUT_MS),
            )
            let first
            try {
              first = await follow.next()
            } finally {
              await follow.return()
            }
            if (first.done || first.value.type !== 'snapshot') {
              return { result: { ok: false, error: { code: 'history-unavailable', message: 'session history snapshot unavailable' } } }
            }
            cursors.set(sessionId, first.value.cursor)
            return { result: { ok: true, value: { events: first.value.records, hasMore: first.value.hasMore } } }
          }
          const throughSeq = takeCursor(sessionId)
          if (throughSeq === undefined) {
            return { result: { ok: false, error: { code: 'history-unavailable', message: 'session history cursor unavailable' } } }
          }
          const page = await sessionController.page(
            { address, throughSeq, beforeSeq, maxMessages },
            AbortSignal.timeout(RPC_TIMEOUT_MS),
          )
          return { result: { ok: true, value: { events: page.records, hasMore: page.hasMore } } }
        } catch (error) {
          return { result: { ok: false, error: envelope(error) } }
        }
      },
    },
  }
}
